import { Injectable, Injector, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { SvcDialogMode } from 'projects/lib-shared-component/src/lib/svc-dialogs/enums/svc-dialog-mode.enum';
import { externalCommonAspURL, externalNetcoreURL } from 'projects/lib-shared-common/src/lib/functions/external-url-redirect';
import { SvcModalBosType } from '../../../svc-modal-bos/shared/enums/svc-modal-bos.enum';
import { DialogAlertType } from '../../../svc-modal-alert/shared/enums/dialog-alert-type.enum';
import { SvcSourceLinkRedirectService } from '../../../../general/svc-source-link/svc-source-link-redirect.service';
import { parseActionPlanSourceRegistry } from '../utils/actionplan-source-registry.utils';

/**
 * Opens the parent record of an action plan from the "Fonte" link — the single
 * source of truth shared by the action plan modal form and the search list, so
 * both open a given source the same way (AB#68126).
 *
 * Dispatch order:
 *  1. Legacy ASP / .NET Core sources — a parseable redirect `sourceLink`
 *     (`RedirectDynamicPage(...)` / ASP) means the record lives in the legacy
 *     module, even when its `sourceDesc` prefix belongs to a revamp module
 *     (AB#68860) — so the redirect always wins.
 *  2. Revamp Angular modules (BOS, Defect Tag, Unsafe Condition, RCA, Alert) —
 *     their records carry a `RevampNotification(...)` link (not parseable as a
 *     redirect) or none; matched by the `sourceDesc` prefix and opened in their
 *     own Angular modal.
 *
 * The parent modal services live inside modals that themselves embed the action
 * plan list/modal, so importing them statically would create a circular
 * dependency. They are therefore loaded with dynamic `import()` (deferred past
 * module-eval) and resolved from the root injector.
 *
 * Out of scope: CIL/CenterLine (no shared revamp modal).
 */
@Injectable({ providedIn: 'root' })
export class SvcActionPlanSourceRedirectService {
  #injector = inject(Injector);
  #sourceLinkRedirect = inject(SvcSourceLinkRedirectService);

  /** `sourceDesc` prefixes whose parent is a revamp module with a shared modal. */
  // AB#73048 Alert records are numbered ALE-<id>; ALR kept for older references
  static readonly REVAMP_PREFIXES = ['BOS', 'DFT', 'UNC', 'RCA', 'ALE', 'ALR'];

  public isRevampPrefix(prefix?: string | null): boolean {
    return !!prefix && SvcActionPlanSourceRedirectService.REVAMP_PREFIXES.includes(prefix.toUpperCase());
  }

  /**
   * Opens the action plan source ("Fonte"):
   *  - legacy sources (ASP or .NET Core) carry a redirect `sourceLink` and open
   *    in a new tab — even when the `sourceDesc` prefix belongs to a revamp
   *    module, since the record only exists in the legacy database (AB#68860);
   *  - revamp Angular modules (BOS, DFT, UNC, RCA, Alert) carry a
   *    `RevampNotification(...)` link (or none) and open in their own Angular
   *    modal on the same page, keyed by the `sourceDesc` prefix.
   */
  public async openSource(sourceDesc?: string | null, sourceLink?: string | null): Promise<void> {
    // Legacy ASP / .NET Core sources: the shared service parses + rebuilds the
    // link (incl. the Digital Obeya issuer-origin URL + Room params) so the
    // redirect targets the right page, opened in a new tab. A parseable
    // redirect link identifies a legacy record regardless of prefix.
    const parsed = this.#sourceLinkRedirect.parse(sourceLink);
    if (parsed) {
      if (parsed.type === 'asp') {
        externalCommonAspURL(parsed.url, parsed.paramsObj);
        return;
      }
      externalNetcoreURL(parsed.url, parsed.paramsStr ?? '');
      return;
    }

    // Revamp Angular modules: open the parent's own modal with the record id
    // taken from `sourceDesc` (e.g. `DFT-231` -> Defect Tag modal, record 231).
    const registry = parseActionPlanSourceRegistry(sourceDesc);
    if (registry && this.isRevampPrefix(registry.prefix)) {
      await this.openParentRegistry(registry.prefix, registry.id);
    }
  }

  /**
   * Opens the revamp modal behind a legacy `RevampNotification('/notification/alert/<id>')`
   * Source link (legacy action plan modal) and returns its dialog ref, or `null` when the path
   * is not handled.
   */
  public async openNotificationSource(url?: string | null): Promise<MatDialogRef<unknown> | null> {
    // AB#73048 the legacy action plan modal Source link of alert records
    const match = /\/notification\/alert\/(\d+)/i.exec(url ?? '');
    const id = Number(match?.[1]);
    if (!Number.isFinite(id) || id <= 0) return null;

    return this.#openAlert(id);
  }

  /**
   * Opens the parent modal for the given `sourceDesc` prefix and record id.
   * Returns `true` when a revamp modal was opened, `false` for an unknown prefix
   * (so the caller can fall back to the legacy redirect).
   */
  public async openParentRegistry(prefix: string, id: number): Promise<boolean> {
    switch (prefix?.toUpperCase()) {
      case 'BOS': {
        const [{ SvcModalBosService }] = await Promise.all([
          import('../../../svc-modal-bos/shared/services/svc-modal-bos.service'),
          import('../../../svc-modal-bos/svc-modal-bos.module'),
        ]);
        this.#injector.get(SvcModalBosService).openModalBos(SvcModalBosType.view, id);
        return true;
      }
      case 'DFT': {
        const [{ SvcModalDefectTagService }] = await Promise.all([
          import('../../../svc-modal-defect-tag/shared/services/svc-modal-defect-tag.service'),
          import('../../../svc-modal-defect-tag/svc-modal-defect-tag.module'),
        ]);
        this.#injector.get(SvcModalDefectTagService).openModalDefectTag({ mode: SvcDialogMode.view, id });
        return true;
      }
      case 'UNC': {
        const [{ SvcModalUnsafeConditionService }] = await Promise.all([
          import('../../../svc-modal-unsafe-condition/shared/services/svc-modal-unsafe-condition.service'),
          import('../../../svc-modal-unsafe-condition/svc-modal-unsafe-condition.module'),
        ]);
        this.#injector.get(SvcModalUnsafeConditionService).openModalUsafeCondition({ mode: SvcDialogMode.view, internalId: id });
        return true;
      }
      case 'RCA': {
        const [{ SvcModalRcaService }] = await Promise.all([
          import('../../../svc-modal-rca/shared/services/svc-modal-rca.service'),
          import('../../../svc-modal-rca/svc-modal-rca.module'),
        ]);
        this.#injector.get(SvcModalRcaService).openModalRca({ mode: SvcDialogMode.view, uniqueId: id });
        return true;
      }
      case 'ALE':
      case 'ALR': {
        await this.#openAlert(id);
        return true;
      }
      default:
        return false;
    }
  }

  async #openAlert(id: number): Promise<MatDialogRef<unknown>> {
    const [{ AlertService }] = await Promise.all([
      import('../../../svc-modal-alert/shared/services/alert.service'),
      import('../../../svc-modal-alert/svc-modal-alert.module'),
    ]);
    return this.#injector.get(AlertService).openAlert(DialogAlertType.view, id);
  }
}
